import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { getApiUrl } from '../config/apiConfig';
import { useAuth } from '../contexts/AuthContext';
import { useCaja } from '../contexts/CajaContext';
import './AperturaCajaModal.css';
import './CierreCajaReporte.css';

const CierreCajaModal = ({ onClose, cajaACerrar: cajaACerrarProp }) => {
  const { auth } = useAuth();
  const { recargarEstadoCaja, cajaActiva } = useCaja();
  // Usar la prop si se pasó (ENCARGADO eligió una caja específica),
  // sino fallback a cajaActiva del contexto (CAJERO con su caja)
  const cajaACerrar = cajaACerrarProp || cajaActiva;
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [conteo, setConteo] = useState({ efectivo: '', yape: '', plin: '', tarjeta: '' });
  const [observaciones, setObservaciones] = useState('');
  const [observacionesFinales, setObservacionesFinales] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Pedidos pendientes — verificación preventiva antes del cierre
  const [pendientesAsignados, setPendientesAsignados] = useState([]); // bloquean el cierre
  const [pendientesSinCaja, setPendientesSinCaja] = useState([]);     // informativo (mesas abiertas)
  const [bloqueosExtra, setBloqueosExtra] = useState([]);
  const [advertenciasExtra, setAdvertenciasExtra] = useState([]);
  const [mostrarAdvertenciaAuditoria, setMostrarAdvertenciaAuditoria] = useState(false);

  // Estado post-cierre
  const [cierreFinalizado, setCierreFinalizado] = useState(false);
  const [reporteData, setReporteData] = useState(null);
  const reporteRef = useRef(null);

  useEffect(() => {
    const fetchDatos = async () => {
      try {
        const idCaja = cajaACerrar?.id;

        // Lanzar ambas consultas en paralelo
        const [resumenRes, pendientesRes] = await Promise.all([
          fetch(
            idCaja ? getApiUrl(`/caja/resumen?id_caja=${idCaja}`) : getApiUrl('/caja/resumen'),
            { headers: { 'Authorization': `Bearer ${auth.token}` } }
          ),
          idCaja
            ? fetch(getApiUrl(`/caja/pedidos-pendientes?id_caja=${idCaja}`), {
                headers: { 'Authorization': `Bearer ${auth.token}` }
              })
            : Promise.resolve(null)
        ]);

        if (!resumenRes.ok) {
          const errData = await resumenRes.json();
          throw new Error(errData.message || 'No se pudo cargar el resumen de ventas.');
        }
        const resumenData = await resumenRes.json();
        setResumen(resumenData);

        if (pendientesRes && pendientesRes.ok) {
          const pendientesData = await pendientesRes.json();
          setPendientesAsignados(pendientesData.asignados || []);
          setPendientesSinCaja(pendientesData.sin_caja || []);
          setBloqueosExtra(pendientesData.bloqueosExtra || []);
          setAdvertenciasExtra(pendientesData.advertenciasExtra || []);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchDatos();
  }, [auth.token, cajaACerrar?.id]);

  const handleConteoChange = (metodo, valor) => {
    setConteo(prev => ({ ...prev, [metodo]: valor }));
    if (mostrarAdvertenciaAuditoria) setMostrarAdvertenciaAuditoria(false);
  };

  const calcularDiferencia = useMemo(() => (metodo) => {
    if (!resumen) return 0;
    const sistema = resumen.desglose[metodo] || 0;
    const contado = parseFloat(conteo[metodo]) || 0;
    return contado - sistema;
  }, [resumen, conteo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSubmitting(true);
    setError('');

    // Validación: el usuario debe haber ingresado al menos un monto
    const algunMontoIngresado = Object.values(conteo).some(v => v !== '');
    if (!algunMontoIngresado) {
      setError('Debes ingresar al menos un monto contado antes de cerrar.');
      setSubmitting(false);
      return;
    }

    const tieneDiferencias = Object.keys(conteo).some(m => calcularDiferencia(m) !== 0);

    // Si hay diferencias y aún no mostramos la advertencia, la mostramos y detenemos el envío preventivamente
    if (tieneDiferencias && !mostrarAdvertenciaAuditoria) {
      setMostrarAdvertenciaAuditoria(true);
      setSubmitting(false);
      return;
    }

    try {
      // Se envía el monto calculado por el sistema a la BD para fines de auditoría, en lugar de lo reportado físicamente.
      const montos_cierre = Object.keys(conteo).map(metodo => ({
        piso: 1,
        monto: resumen.desglose[metodo] || 0,
        metodo: metodo
      }));

      // Las diferencias físicas se anexan a las observaciones
      let observacionesDiferencia = observaciones;
      
      if (tieneDiferencias) {
        observacionesDiferencia += '\n\n--- REPORTE FÍSICO Y DIFERENCIAS DECLARADAS ---\n';
        Object.keys(conteo).forEach(metodo => {
          const diff = calcularDiferencia(metodo);
          if (diff !== 0) {
            observacionesDiferencia += `• ${metodo.toUpperCase()}: Sistema S/. ${(resumen.desglose[metodo] || 0).toFixed(2)} | Físico S/. ${(parseFloat(conteo[metodo]) || 0).toFixed(2)} -> Diferencia: S/. ${diff.toFixed(2)}\n`;
          }
        });
      }

      setObservacionesFinales(observacionesDiferencia.trim());

      const response = await fetch(getApiUrl('/caja/cerrar'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.token}` },
        body: JSON.stringify({
          id_caja: resumen.id_caja,
          montos_cierre: montos_cierre,
          observaciones: observacionesDiferencia.trim()
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Error al procesar el cierre.');
      }

      // Obtener reporte del turno recién cerrado
      const reporteRes = await fetch(getApiUrl(`/caja/reporte?id_caja=${resumen.id_caja}`), {
        headers: { 'Authorization': `Bearer ${auth.token}` }
      });
      if (reporteRes.ok) {
        const data = await reporteRes.json();
        setReporteData(data);
      } else {
        // Mostrar error en el panel post-cierre pero no bloquear el flujo
        const errData = await reporteRes.json().catch(() => ({}));
        setError(errData.message || 'No se pudo cargar el reporte del turno.');
      }

      // Mostrar el panel post-cierre. NO se llama recargarEstadoCaja() aquí
      // porque eso cambia caja.estaAbierta → false, lo que hace que CajaComponent
      // desmonte el modal antes de que el usuario pueda descargar el reporte.
      // La recarga del contexto se hace cuando el usuario hace click en "Cerrar".
      setCierreFinalizado(true);

    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // --- Generación Excel ---
  const descargarExcel = () => {
    if (!reporteData) return;

    const wb = XLSX.utils.book_new();

    // Hoja 1: Detalle de productos
    const encabezadoProductos = [
      ['REPORTE DE CIERRE DE CAJA'],
      [`Cajero: ${reporteData.cajero || '-'}`],
      [`Apertura: ${new Date(reporteData.fecha_apertura).toLocaleString()}`],
      [`Cierre: ${reporteData.fecha_cierre ? new Date(reporteData.fecha_cierre).toLocaleString() : '-'}`],
      [],
      ['Producto', 'Cantidad', 'Precio Unit. (S/.)', 'Subtotal (S/.)'],
    ];
    const filasProductos = reporteData.productos.map(p => [
      p.nombre,
      p.cantidad_total,
      parseFloat(p.precio_unitario).toFixed(2),
      parseFloat(p.subtotal).toFixed(2),
    ]);
    filasProductos.push([]);
    filasProductos.push(['', '', 'TOTAL GENERAL:', parseFloat(reporteData.total_general).toFixed(2)]);

    const wsProductos = XLSX.utils.aoa_to_sheet([...encabezadoProductos, ...filasProductos]);
    wsProductos['!cols'] = [{ wch: 30 }, { wch: 12 }, { wch: 20 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, wsProductos, 'Ventas por Producto');

    // Hoja 2: Resumen por método de pago
    const filasPagos = [
      ['RESUMEN POR MÉTODO DE PAGO'],
      [],
      ['Método de Pago', 'Total (S/.)'],
      ...(reporteData.monto_apertura > 0 ? [['Fondo de Apertura', parseFloat(reporteData.monto_apertura).toFixed(2)]] : []),
      ...Object.entries(reporteData.resumen_pagos).map(([metodo, total]) => [
        metodo.charAt(0).toUpperCase() + metodo.slice(1),
        parseFloat(total).toFixed(2),
      ]),
      [],
      ['TOTAL EN CAJA', (parseFloat(reporteData.total_general) + parseFloat(reporteData.monto_apertura || 0)).toFixed(2)],
    ];

    if (observacionesFinales) {
      filasPagos.push([]);
      filasPagos.push(['OBSERVACIONES DE CIERRE']);
      const obsLines = observacionesFinales.split('\n');
      obsLines.forEach(line => filasPagos.push([line]));
    }

    const wsPagos = XLSX.utils.aoa_to_sheet(filasPagos);
    wsPagos['!cols'] = [{ wch: 45 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, wsPagos, 'Resumen de Pagos');

    // Hoja 3: Cancelaciones (si existen)
    if (reporteData.cancelaciones && reporteData.cancelaciones.length > 0) {
      const encabezadoCancelaciones = [
        ['AUDITORÍA DE PRODUCTOS CANCELADOS'],
        [],
        ['Mesa/Pedido', 'Producto', 'Cantidad', 'Precio Unit. (S/.)', 'Motivo', 'Cancelado Por', 'Fecha/Hora'],
      ];
      const filasCancelaciones = reporteData.cancelaciones.map(c => [
        c.referencia_mesa,
        c.nombre_producto,
        c.cantidad,
        parseFloat(c.precio_unitario).toFixed(2),
        c.motivo,
        c.usuario_cancelo || '-',
        new Date(c.fecha_cancelacion).toLocaleString()
      ]);
      const wsCancelaciones = XLSX.utils.aoa_to_sheet([...encabezadoCancelaciones, ...filasCancelaciones]);
      wsCancelaciones['!cols'] = [{ wch: 20 }, { wch: 30 }, { wch: 10 }, { wch: 18 }, { wch: 35 }, { wch: 25 }, { wch: 22 }];
      XLSX.utils.book_append_sheet(wb, wsCancelaciones, 'Cancelaciones');
    }

    const fecha = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `reporte-caja-${fecha}.xlsx`);
  };

  // --- Generación PDF (ventana nueva de impresión) ---
  const descargarPDF = () => {
    if (!reporteData) return;

    const ventana = window.open('', '_blank', 'width=800,height=600');
    ventana.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Reporte de Cierre de Caja</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 13px; color: #333; margin: 24px; }
          h2 { color: #f05b41; font-size: 1.2rem; margin: 0 0 6px; }
          h4 { font-size: 0.95rem; text-transform: uppercase; letter-spacing: 0.5px;
               border-bottom: 1px solid #eee; padding-bottom: 4px; margin: 14px 0 8px; }
          p { margin: 3px 0; font-size: 0.9rem; color: #555; }
          .encabezado { border-bottom: 2px solid #f05b41; padding-bottom: 10px; margin-bottom: 16px; }
          table { width: 100%; border-collapse: collapse; font-size: 0.88rem; margin-bottom: 10px; }
          th { background: #f5f5f5; font-weight: 600; text-align: left;
               padding: 7px 10px; border-bottom: 2px solid #ddd; }
          td { padding: 6px 10px; border-bottom: 1px solid #f0f0f0; }
          .pagos { max-width: 360px; }
          tfoot td { font-weight: bold; border-top: 2px solid #f05b41;
                     font-size: 0.95rem; color: #f05b41; }
        </style>
      </head>
      <body>
        <div class="encabezado">
          <h2>Reporte de Cierre de Caja</h2>
          <p><strong>Cajero:</strong> ${reporteData.cajero || '-'}</p>
          <p><strong>Apertura:</strong> ${new Date(reporteData.fecha_apertura).toLocaleString()}</p>
          <p><strong>Cierre:</strong> ${reporteData.fecha_cierre ? new Date(reporteData.fecha_cierre).toLocaleString() : '-'}</p>
        </div>

        <h4>Ventas por Producto</h4>
        <table>
          <thead><tr><th>Producto</th><th>Cantidad</th><th>Precio Unit.</th><th>Subtotal</th></tr></thead>
          <tbody>
            ${reporteData.productos.map(p => `
              <tr>
                <td>${p.nombre}</td>
                <td>${p.cantidad_total}</td>
                <td>S/. ${parseFloat(p.precio_unitario).toFixed(2)}</td>
                <td>S/. ${parseFloat(p.subtotal).toFixed(2)}</td>
              </tr>`).join('')}
          </tbody>
        </table>

        <h4>Resumen por Método de Pago</h4>
        <table class="pagos">
          <thead><tr><th>Método de Pago</th><th>Total</th></tr></thead>
          <tbody>
            ${reporteData.monto_apertura > 0 ? `
              <tr>
                <td>🏦 Fondo de Apertura</td>
                <td>S/. ${parseFloat(reporteData.monto_apertura).toFixed(2)}</td>
              </tr>` : ''}
            ${Object.entries(reporteData.resumen_pagos).map(([metodo, total]) => `
              <tr>
                <td>${metodo.charAt(0).toUpperCase() + metodo.slice(1)}</td>
                <td>S/. ${parseFloat(total).toFixed(2)}</td>
              </tr>`).join('')}
          </tbody>
          <tfoot>
            <tr><td>TOTAL EN CAJA</td><td>S/. ${(parseFloat(reporteData.total_general) + parseFloat(reporteData.monto_apertura || 0)).toFixed(2)}</td></tr>
          </tfoot>
        </table>

        ${reporteData.cancelaciones && reporteData.cancelaciones.length > 0 ? `
          <h4>Auditoría de Cancelaciones</h4>
          <table>
            <thead>
              <tr>
                <th>Mesa/Pedido</th>
                <th>Producto</th>
                <th>Cant.</th>
                <th>Motivo</th>
                <th>Cancelado por</th>
                <th>Fecha/Hora</th>
              </tr>
            </thead>
            <tbody>
              ${reporteData.cancelaciones.map(c => `
                <tr>
                  <td>${c.referencia_mesa} ${c.id_pedido ? `(Ped. #${c.id_pedido})` : ''}</td>
                  <td>${c.nombre_producto}</td>
                  <td>${c.cantidad}</td>
                  <td>${c.motivo}</td>
                  <td>${c.usuario_cancelo || '-'}</td>
                  <td>${new Date(c.fecha_cancelacion).toLocaleString()}</td>
                </tr>`).join('')}
            </tbody>
          </table>
        ` : ''}

        ${observacionesFinales ? `
          <h4 style="margin-top: 20px; color: #f05b41; border-bottom: 2px solid #eee; padding-bottom: 4px;">Observaciones de Cierre</h4>
          <div style="background-color: #f9f9f9; padding: 12px; border-radius: 6px; border: 1px solid #eaeaea; white-space: pre-wrap; font-family: monospace; font-size: 0.9rem; margin-top: 10px;">${observacionesFinales.replace(/\n/g, '<br/>')}</div>
        ` : ''}
      </body>
      </html>
    `);
    ventana.document.close();
    ventana.focus();
    ventana.print();
  };

  // --- Renderizado: formulario de cierre ---
  const renderFormulario = () => {
    if (loading) return <div className="loading-text">Cargando resumen de ventas...</div>;
    if (error && !resumen) return <div className="error-message">{error}</div>;
    if (!resumen) return <div className="error-message">No se pudo obtener el resumen.</div>;

    return (
      <form onSubmit={handleSubmit}>
        {/* ── Advertencia BLOQUEANTE: pedidos asignados a esta caja sin pagar ── */}
        {pendientesAsignados.length > 0 && (
          <div className="aviso-pendientes aviso-bloqueante">
            <div className="aviso-pendientes-titulo">
              🚫 {pendientesAsignados.length} pedido{pendientesAsignados.length !== 1 ? 's' : ''} sin pagar en esta caja
            </div>
            <p className="aviso-pendientes-desc">
              No se puede cerrar la caja. Cobra o cancela estos pedidos primero.
            </p>
            <ul className="lista-pendientes">
              {pendientesAsignados.map(p => (
                <li key={p.id}>
                  <span className="pend-id">Pedido #{p.id}</span>
                  <span className="pend-mesa">Mesa {p.numero_mesa}</span>
                  <span className="pend-estado">{p.estado.replace(/_/g, ' ')}</span>
                  <span className="pend-total">S/. {parseFloat(p.total || 0).toFixed(2)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ── ALERTS POR BLOQUEOS EXTRA ── */}
        {bloqueosExtra.length > 0 && (
          <div className="aviso-pendientes aviso-bloqueante">
            <div className="aviso-pendientes-titulo">
              🚫 Seguridad Avanzada: Cierre Bloqueado
            </div>
            <ul className="lista-pendientes" style={{ paddingLeft: '0' }}>
              {bloqueosExtra.map((b, i) => <li key={i} style={{ color: '#9b2c2c', fontWeight: 'bold' }}>{b}</li>)}
            </ul>
          </div>
        )}

        {/* ── ALERTS POR ADVERTENCIAS EXTRA ── */}
        {advertenciasExtra.length > 0 && (
          <div className="aviso-pendientes aviso-informativo">
            <div className="aviso-pendientes-titulo">
              ℹ️ Información de Turno Compartido
            </div>
            <ul className="lista-pendientes" style={{ paddingLeft: '0' }}>
              {advertenciasExtra.map((a, i) => <li key={i} style={{ color: '#9c4221' }}>{a}</li>)}
            </ul>
          </div>
        )}

        {/* ── Advertencia INFORMATIVA: mesas abiertas sin caja asignada ── */}
        {pendientesSinCaja.length > 0 && (
          <div className="aviso-pendientes aviso-informativo">
            <div className="aviso-pendientes-titulo">
              ⚠️ {pendientesSinCaja.length} mesa{pendientesSinCaja.length !== 1 ? 's' : ''} con pedidos sin cobrar
            </div>
            <p className="aviso-pendientes-desc">
              Estos pedidos no están asignados a ninguna caja. Puedes cerrar, pero asegúrate de que sean manejados por otra caja o turno.
            </p>
            <ul className="lista-pendientes">
              {pendientesSinCaja.map(p => (
                <li key={p.id}>
                  <span className="pend-id">Pedido #{p.id}</span>
                  <span className="pend-mesa">Mesa {p.numero_mesa}</span>
                  <span className="pend-estado">{p.estado.replace(/_/g, ' ')}</span>
                  <span className="pend-total">S/. {parseFloat(p.total || 0).toFixed(2)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {resumen.monto_apertura > 0 && (
          <p className="total-ventas">Fondo de Apertura: <strong>S/. {parseFloat(resumen.monto_apertura).toFixed(2)}</strong></p>
        )}
        <p className="total-ventas">Total Ventas del Turno: <strong>S/. {resumen.totalGeneral.toFixed(2)}</strong></p>
        <p className="total-ventas">Total en Caja: <strong>S/. {(parseFloat(resumen.totalGeneral) + parseFloat(resumen.monto_apertura || 0)).toFixed(2)}</strong></p>
        <table className="cierre-tabla">
          <thead>
            <tr>
              <th>Método de Pago</th>
              <th>Venta (Sistema)</th>
              <th>Monto (Contado)</th>
              <th>Diferencia</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(resumen.desglose).map(metodo => (
              <tr key={metodo}>
                <td>{metodo.charAt(0).toUpperCase() + metodo.slice(1)}</td>
                <td>S/. {resumen.desglose[metodo].toFixed(2)}</td>
                <td>
                  <input type="number" min="0" step="0.01" placeholder="0.00" value={conteo[metodo]} onChange={(e) => handleConteoChange(metodo, e.target.value)} />
                </td>
                <td className={calcularDiferencia(metodo) < 0 ? 'negativo' : 'positivo'}>
                  S/. {calcularDiferencia(metodo).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {error && <div className="error-message" style={{ marginTop: '0.5rem' }}>{error}</div>}
        
        {mostrarAdvertenciaAuditoria && (
          <div className="aviso-pendientes aviso-informativo" style={{ marginTop: '1rem', backgroundColor: '#fffbeb', color: '#b45309', borderLeft: '4px solid #f59e0b' }}>
            <div className="aviso-pendientes-titulo">
              ⚠️ Diferencias Detectadas en Caja
            </div>
            <p className="aviso-pendientes-desc" style={{ color: '#92400e', marginTop: '0.4rem', fontSize: '0.9rem' }}>
              El sistema ha detectado un descuadre entre el monto calculado y el monto físico contado.
              <br/><br/>
              <strong>IMPORTANTE:</strong> Para mantener un registro preciso, <strong>el sistema grabará los montos automáticos (Venta Sistema).</strong> Las diferencias serán registradas textualmente como nota en las observaciones y deberás arreglarlas internamente con tu encargado al finalizar el turno.
              <br/><br/>
              <em>Si estás de acuerdo con registrar esta diferencia en caja, presiona "Confirmar Cierre de Caja" nuevamente para continuar.</em>
            </p>
          </div>
        )}

        <div className="form-group" style={{ marginTop: '1rem' }}>
          <label>Observaciones de Cierre</label>
          <textarea rows="3" placeholder="Anotaciones sobre diferencias o eventos del turno..." value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
        </div>
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={() => onClose(false)} disabled={submitting}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={submitting || pendientesAsignados.length > 0 || bloqueosExtra.length > 0}>
            {pendientesAsignados.length > 0 || bloqueosExtra.length > 0
              ? `Cierre Bloqueado (Ver advertencias arriba)`
              : submitting ? 'Cerrando...' : 'Confirmar Cierre de Caja'
            }
          </button>
        </div>
      </form>
    );
  };

  // --- Renderizado: panel post-cierre con descarga ---
  const renderPostCierre = () => (
    <div className="cierre-exitoso-panel">
      <div className="cierre-exitoso-icono">✓</div>
      <h3>¡Caja cerrada exitosamente!</h3>
      <p className="cierre-exitoso-texto">Descarga el reporte del turno antes de cerrar esta ventana.</p>

      {!reporteData && error && (
        <div className="error-message" style={{ margin: '1rem 0' }}>{error}</div>
      )}

      {reporteData && (
        <>
          {/* Área imprimible — visible en pantalla y en impresión */}
          <div ref={reporteRef} id="reporte-caja-imprimible">
            <div className="reporte-encabezado">
              <h2>Reporte de Cierre de Caja</h2>
              <p><strong>Cajero:</strong> {reporteData.cajero || '-'}</p>
              <p><strong>Apertura:</strong> {new Date(reporteData.fecha_apertura).toLocaleString()}</p>
              <p><strong>Cierre:</strong> {reporteData.fecha_cierre ? new Date(reporteData.fecha_cierre).toLocaleString() : '-'}</p>
            </div>

            <h4 className="reporte-seccion-titulo">Ventas por Producto</h4>
            <table className="reporte-tabla">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Cantidad</th>
                  <th>Precio Unit.</th>
                  <th>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {reporteData.productos.map((p, i) => (
                  <tr key={i}>
                    <td>{p.nombre}</td>
                    <td>{p.cantidad_total}</td>
                    <td>S/. {parseFloat(p.precio_unitario).toFixed(2)}</td>
                    <td>S/. {parseFloat(p.subtotal).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h4 className="reporte-seccion-titulo">Resumen por Método de Pago</h4>
            <table className="reporte-tabla reporte-tabla-pagos">
              <thead>
                <tr>
                  <th>Método de Pago</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {reporteData.monto_apertura > 0 && (
                  <tr>
                    <td>🏦 Fondo de Apertura</td>
                    <td>S/. {parseFloat(reporteData.monto_apertura).toFixed(2)}</td>
                  </tr>
                )}
                {Object.entries(reporteData.resumen_pagos).map(([metodo, total]) => (
                  <tr key={metodo}>
                    <td>{metodo.charAt(0).toUpperCase() + metodo.slice(1)}</td>
                    <td>S/. {parseFloat(total).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="reporte-total-row">
                  <td><strong>TOTAL EN CAJA</strong></td>
                  <td><strong>S/. {(parseFloat(reporteData.total_general) + parseFloat(reporteData.monto_apertura || 0)).toFixed(2)}</strong></td>
                </tr>
              </tfoot>
            </table>

            {reporteData.cancelaciones && reporteData.cancelaciones.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <h4 className="reporte-seccion-titulo">Auditoría de Cancelaciones</h4>
                <table className="reporte-tabla">
                  <thead>
                    <tr>
                      <th>Mesa/Pedido</th>
                      <th>Producto</th>
                      <th>Cant.</th>
                      <th>Motivo</th>
                      <th>Cancelado por</th>
                      <th>Fecha/Hora</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reporteData.cancelaciones.map((c, i) => (
                      <tr key={i}>
                        <td>{c.referencia_mesa} {c.id_pedido ? `(Ped. #${c.id_pedido})` : ''}</td>
                        <td>{c.nombre_producto}</td>
                        <td>{c.cantidad}</td>
                        <td>{c.motivo}</td>
                        <td>{c.usuario_cancelo || '-'}</td>
                        <td>{new Date(c.fecha_cancelacion).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {observacionesFinales && (
              <div style={{ marginTop: '20px' }}>
                <h4 className="reporte-seccion-titulo">Observaciones de Cierre</h4>
                <div style={{ backgroundColor: '#f9f9f9', padding: '12px', borderRadius: '6px', border: '1px solid #eaeaea', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.9rem', color: '#444' }}>
                  {observacionesFinales}
                </div>
              </div>
            )}
          </div>

          <div className="cierre-botones-reporte">
            <button className="btn-reporte btn-excel" onClick={descargarExcel}>
              ⬇ Descargar Excel
            </button>
            <button className="btn-reporte btn-pdf" onClick={descargarPDF}>
              🖨 Descargar PDF
            </button>
          </div>
        </>
      )}

      <div className="modal-actions" style={{ marginTop: '1rem' }}>
        <button className="btn-primary" onClick={() => { recargarEstadoCaja(); onClose(true); }}>Cerrar</button>
      </div>
    </div>
  );

  return (
    <div className="modal-backdrop">
      <div className={`modal-content ${cierreFinalizado ? 'modal-content-reporte' : ''}`}>
        <h2>Cerrar Caja — {cajaACerrar?.nombre || `Caja #${cajaACerrar?.id}` || 'Caja'}</h2>
        {cierreFinalizado ? renderPostCierre() : renderFormulario()}
      </div>
    </div>
  );
};

export default CierreCajaModal;
